import React, { useState, useEffect } from 'react';
import { ArrowUpTrayIcon, DocumentArrowDownIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DataImports = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchImportHistory();
    } else if (activeTab === 'preview' && uploadProgress?.sessionId) {
      fetchPreviewData(uploadProgress.sessionId);
    }
  }, [activeTab, uploadProgress?.sessionId]);

  const fetchImportHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/import/history');
      setImportHistory(response.data.import_sessions || []);
    } catch (error) {
      console.error('Error fetching import history:', error);
      toast.error('Failed to load import history');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewData = async (sessionId) => {
    try {
      setPreviewLoading(true);
      const response = await api.get(`/import/preview/${sessionId}`);
      setPreviewData(response.data);
      setValidationErrors(response.data.validation_errors || []);
    } catch (error) {
      console.error('Error fetching preview data:', error);
      toast.error('Failed to load preview data');
    } finally {
      setPreviewLoading(false);
    }
  };

  const rollbackImport = async (sessionId) => {
    if (!window.confirm('Are you sure you want to rollback this import? This will permanently delete all data imported in this session.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/import/rollback/${sessionId}`);
      
      if (response.data.success) {
        toast.success(`Successfully rolled back import (${response.data.records_deleted} records removed)`);
        fetchImportHistory(); // Refresh the list
      } else {
        toast.error(response.data.error || 'Failed to rollback import');
      }
    } catch (error) {
      console.error('Error rolling back import:', error);
      toast.error(error.response?.data?.error || 'Failed to rollback import');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await api.get(`/import/template/${type}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_import_template.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success(`${type} template downloaded`);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const downloadMissingEmailReport = async (sessionId) => {
    try {
      const response = await api.get(`/import/contacts/missing-email/${sessionId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_missing_email_${sessionId}.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success('Missing email report downloaded');
    } catch (error) {
      console.error('Error downloading missing email report:', error);
      toast.error('Failed to download missing email report');
    }
  };

  const handleFileUpload = async (event, importType) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadProgress({ type: importType, status: 'uploading' });
      
      const response = await api.post(`/import/${importType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadProgress({ 
        type: importType, 
        status: 'success', 
        sessionId: response.data.session_id,
        summary: response.data.summary
      });
      
      toast.success(`${importType} file uploaded successfully! Check the preview.`);
      
      // Switch to preview tab
      setActiveTab('preview');
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress({ type: importType, status: 'error', error: error.response?.data?.error || error.message });
      toast.error(`Failed to upload ${importType} file`);
    }

    // Reset file input
    event.target.value = '';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const confirmImport = async () => {
    if (!uploadProgress?.sessionId) return;

    try {
      setPreviewLoading(true);
      const response = await api.post(`/import/confirm/${uploadProgress.sessionId}`);
      
      toast.success(`Import confirmed! ${response.data.records_processed} records processed.`);
      
      // Clear upload progress and refresh history
      setUploadProgress(null);
      setPreviewData(null);
      setValidationErrors([]);
      setActiveTab('history');
      
    } catch (error) {
      console.error('Error confirming import:', error);
      toast.error(`Failed to confirm import: ${error.response?.data?.error || error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Data Imports</h1>
        <p className="text-gray-600 mt-2">
          Import revenue, employee, and contact data with automatic consolidation for multi-paygroup clients.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('upload')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upload'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upload Files
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'preview'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Preview & Confirm
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            History & Rollback
          </button>
        </nav>
      </div>

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="space-y-8">
          {/* Templates Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-4">📋 Download Templates</h3>
            <p className="text-blue-700 mb-4">
              Start by downloading the correct CSV template for your import type.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => downloadTemplate('revenue')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Revenue Template
              </button>
              <button
                onClick={() => downloadTemplate('employees')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Employee Template
              </button>
              <button
                onClick={() => downloadTemplate('contacts')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Contact Template
              </button>
            </div>
          </div>

          {/* Upload Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue Upload */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">💰 Revenue Import</h3>
              <p className="text-gray-600 mb-4">
                Upload monthly revenue data. Multiple paygroups for the same client will be automatically consolidated.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-indigo-600 hover:text-indigo-500">Upload revenue CSV</span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileUpload(e, 'revenue')}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">CSV files only, max 10MB</p>
              </div>
            </div>

            {/* Employee Upload */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">👥 Employee Import</h3>
              <p className="text-gray-600 mb-4">
                Upload monthly employee counts. Multiple paygroups for the same client will be automatically consolidated.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-indigo-600 hover:text-indigo-500">Upload employee CSV</span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileUpload(e, 'employees')}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">CSV files only, max 10MB</p>
              </div>
            </div>
          </div>

          {/* Contact Import */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Import</h3>
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Upload contact data by client code. <strong>Email is required</strong> and serves as the unique key - existing contacts with the same email will be updated.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-indigo-600 hover:text-indigo-500">Upload contact CSV</span>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileUpload(e, 'contacts')}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">CSV files only, max 10MB</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Expected Format:</h4>
                <pre className="text-xs text-gray-600 bg-white p-2 rounded border overflow-x-auto">
{`client_code,name,last_name,title,email,phone,mobile_phone,is_primary
ABC001,John,Doe,Manager,john.doe@company.com,240-123-0060,240-123-0061,true
ABC001,Jane,Smith,Assistant,jane.smith@company.com,240-123-0062,,false
DEF456,Bob,Johnson,Owner,bob.johnson@company2.com,240-123-0063,240-123-0064,true`}
                </pre>
              </div>
            </div>
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Status</h3>
              {uploadProgress.status === 'uploading' && (
                <div className="flex items-center text-blue-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600 mr-2"></div>
                  Uploading {uploadProgress.type} file...
                </div>
              )}
              {uploadProgress.status === 'success' && (
                <div className="space-y-3">
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    Upload successful!
                  </div>
                  <div className="bg-green-50 p-4 rounded-md">
                    <h4 className="font-medium">Import Summary:</h4>
                    <ul className="text-sm mt-2 space-y-1">
                      <li>Total records: {uploadProgress.summary?.total_records}</li>
                      <li>Valid records: {uploadProgress.summary?.valid_records}</li>
                      <li>Invalid records: {uploadProgress.summary?.invalid_records}</li>
                      {uploadProgress.summary?.missing_email_records > 0 && (
                        <li>Missing email: {uploadProgress.summary?.missing_email_records}</li>
                      )}
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600">
                    Switch to the "Preview & Confirm" tab to review and confirm your import.
                  </p>
                </div>
              )}
              {uploadProgress.status === 'error' && (
                <div className="text-red-600">
                  <div className="flex items-center mb-2">
                    <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                    Upload failed
                  </div>
                  <p className="text-sm">{uploadProgress.error}</p>
                </div>
              )}
            </div>
          )}

          {/* CSV Format Guide */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">📝 CSV Format Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Revenue CSV Format:</h4>
                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`client_code,paygroup_name,month,amount,notes
ABC001,Main Office,2024-01,5000.00,Monthly billing
ABC001,Satellite,2024-01,2000.00,Additional location`}
                </pre>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Employee CSV Format:</h4>
                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`client_code,paygroup_name,month,employee_count,notes
ABC001,Main Office,2024-01,15,Primary workforce
ABC001,Satellite,2024-01,5,Remote employees`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div>
          {!uploadProgress?.sessionId ? (
            <div className="text-center py-12">
              <ArrowUpTrayIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Import to Preview</h3>
              <p className="text-gray-600">
                Upload a CSV file first to see the consolidation preview here.
              </p>
            </div>
          ) : previewLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading preview data...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mr-2" />
                    <h3 className="text-lg font-medium text-red-900">
                      Validation Errors ({validationErrors.length} rows)
                    </h3>
                  </div>
                  <p className="text-red-700 mb-4">
                    The following rows have errors and will not be imported. Please fix these issues and re-upload your file.
                  </p>
                  <div className="bg-white border border-red-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-red-200">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Row #</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Client Code</th>
                            {previewData?.session?.import_type === 'contacts' ? (
                              <>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Contact Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Issue</th>
                              </>
                            ) : (
                              <>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Paygroup</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Month</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Value</th>
                              </>
                            )}
                            <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">Errors</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-red-200">
                          {validationErrors.map((error, index) => (
                            <tr key={index} className="hover:bg-red-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{error.row || error.row_number}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{error.client_code || 'Missing'}</td>
                              {previewData?.session?.import_type === 'contacts' ? (
                                <>
                                  <td className="px-4 py-3 text-sm text-gray-900">{error.contact_name || 'Missing'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">{error.email || 'Missing'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {error.is_missing_email ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Missing Email
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Validation Error
                                      </span>
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-4 py-3 text-sm text-gray-900">{error.paygroup_name || 'Missing'}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {previewData?.session?.import_type === 'revenue' ? error.revenue_month : error.employee_month}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {previewData?.session?.import_type === 'revenue' ? error.amount : error.employee_count}
                                  </td>
                                </>
                              )}
                              <td className="px-4 py-3 text-sm text-red-600">
                                {error.errors && Array.isArray(error.errors) ? (
                                  <ul className="list-disc list-inside space-y-1">
                                    {error.errors.map((err, i) => (
                                      <li key={i}>{err}</li>
                                    ))}
                                  </ul>
                                ) : error.validation_errors?.errors ? (
                                  <ul className="list-disc list-inside space-y-1">
                                    {error.validation_errors.errors.map((err, i) => (
                                      <li key={i}>{err}</li>
                                    ))}
                                  </ul>
                                ) : error.is_missing_email ? (
                                  <div className="text-red-600">Email is required for contact imports</div>
                                ) : 'Unknown error'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Consolidation Preview */}
              {previewData?.consolidated_data && previewData.consolidated_data.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <CheckCircleIcon className="w-6 h-6 text-green-600 mr-2" />
                    <h3 className="text-lg font-medium text-green-900">
                      Consolidation Preview ({previewData.consolidated_data.length} records)
                    </h3>
                  </div>
                  <p className="text-green-700 mb-4">
                    {previewData?.session?.import_type === 'contacts' 
                      ? 'The following contacts will be imported:' 
                      : 'The following data will be imported with paygroup consolidation applied:'}
                  </p>
                  <div className="bg-white border border-green-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-green-200">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Client</th>
                            {previewData?.session?.import_type === 'contacts' ? (
                              <>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Will Create</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Will Update</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Contacts</th>
                              </>
                            ) : (
                              <>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Month</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">
                                  {previewData?.session?.import_type === 'revenue' ? 'Total Amount' : 'Total Employees'}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Paygroups</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-green-900 uppercase tracking-wider">Details</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-green-200">
                          {previewData.consolidated_data.map((item, index) => (
                            <tr key={index} className="hover:bg-green-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.client_name}</div>
                                  <div className="text-xs text-gray-500">Code: {item.client_code}</div>
                                </div>
                              </td>
                              {previewData?.session?.import_type === 'contacts' ? (
                                <>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {item.will_create_count || 0} new
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {item.will_update_count || 0} update
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    <div className="max-w-xs">
                                      <div className="text-xs truncate">{item.contacts}</div>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-4 py-3 text-sm text-gray-900">{item.revenue_month || item.employee_month}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {previewData?.session?.import_type === 'revenue' 
                                      ? `$${parseFloat(item.consolidated_amount || 0).toLocaleString()}` 
                                      : `${item.consolidated_count || 0} employees`}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                      {item.paygroup_count} paygroup{item.paygroup_count !== 1 ? 's' : ''}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-500">
                                    <div className="max-w-xs">
                                      <div className="text-xs">Paygroups: {item.paygroups}</div>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary and Actions */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Import Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{uploadProgress?.summary?.total_records || 0}</div>
                    <div className="text-sm text-blue-600">Total Rows</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{uploadProgress?.summary?.valid_records || 0}</div>
                    <div className="text-sm text-green-600">Valid Rows</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{uploadProgress?.summary?.invalid_records || 0}</div>
                    <div className="text-sm text-red-600">Invalid Rows</div>
                  </div>
                  {previewData?.session?.import_type === 'contacts' && uploadProgress?.summary?.missing_email_records > 0 && (
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">{uploadProgress?.summary?.missing_email_records || 0}</div>
                      <div className="text-sm text-orange-600">Missing Email</div>
                    </div>
                  )}
                </div>
                
                {previewData?.summary?.can_proceed ? (
                  <div className="flex justify-center space-x-4">
                    {previewData?.session?.import_type === 'contacts' && uploadProgress?.summary?.missing_email_records > 0 && (
                      <button 
                        onClick={() => downloadMissingEmailReport(uploadProgress.sessionId)}
                        className="bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        Download Missing Email Report ({uploadProgress?.summary?.missing_email_records})
                      </button>
                    )}
                    <button 
                      onClick={confirmImport}
                      disabled={previewLoading}
                      className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {previewLoading ? 'Processing...' : `Confirm Import (${uploadProgress?.summary?.valid_records} records)`}
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-red-600">
                    <p className="mb-2">Cannot proceed with import due to validation errors.</p>
                    <p className="text-sm">Please fix the errors above and re-upload your file.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading import history...</p>
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Import History</h3>
              <p className="text-gray-600">
                Your import history will appear here after you complete your first import.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Recent Imports & Rollback Options</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {importHistory.map((session) => (
                  <div key={session.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {getStatusIcon(session.status)}
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {session.import_type} Import
                          </h4>
                          <p className="text-sm text-gray-600">{session.filename}</p>
                          {session.status === 'undone' && (
                            <p className="text-xs text-orange-600 font-medium">
                              ✓ Rolled back on {formatDate(session.undone_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-900">{formatDate(session.started_at)}</p>
                          <p className="text-xs text-gray-500">
                            {session.records_created} records created
                          </p>
                          {session.can_undo && session.status === 'completed' && session.undo_expires_at && (
                            <p className="text-xs text-amber-600">
                              Rollback expires: {formatDate(session.undo_expires_at)}
                            </p>
                          )}
                        </div>
                        {session.can_undo && session.status === 'completed' && (
                          <button
                            onClick={() => rollbackImport(session.id)}
                            disabled={loading}
                            className="flex items-center space-x-1 bg-red-600 text-white text-sm px-3 py-1 rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <ArrowUturnLeftIcon className="w-4 h-4" />
                            <span>Rollback</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataImports;
